// hooks/useData.js
import { useState, useEffect, useCallback } from 'react';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const cache = { meetings: null, actions: null, lastFetched: null };
const CACHE_TTL = 30000;

function getUserId() { return window.__clerk_user_id || 'default'; }

async function fetchAll() {
  const headers = { 'x-user-id': getUserId() };
  const [meetings, actions] = await Promise.all([
    fetch(`${BASE}/api/meetings`, { headers }).then(r => r.json()).catch(() => []),
    fetch(`${BASE}/api/actions`,  { headers }).then(r => r.json()).catch(() => []),
  ]);
  cache.meetings = Array.isArray(meetings) ? meetings : [];
  cache.actions  = Array.isArray(actions)  ? actions  : [];
  cache.lastFetched = Date.now();
  return { meetings: cache.meetings, actions: cache.actions };
}

// Listeners to notify all useData instances when cache updates
const listeners = new Set();
function notifyListeners() {
  listeners.forEach(fn => fn());
}

export function useData(refreshTrigger) {
  const [data, setData] = useState({
    meetings: cache.meetings || [],
    actions:  cache.actions  || [],
    loading:  !cache.meetings,
  });

  const sync = useCallback(() => {
    if (cache.meetings) {
      setData({ meetings: cache.meetings, actions: cache.actions, loading: false });
    }
  }, []);

  // Subscribe to cache updates
  useEffect(() => {
    listeners.add(sync);
    return () => listeners.delete(sync);
  }, [sync]);

  const load = useCallback(async (force = false) => {
    const stale = !cache.lastFetched || Date.now() - cache.lastFetched > CACHE_TTL;
    if (!force && !stale && cache.meetings) {
      setData({ meetings: cache.meetings, actions: cache.actions, loading: false });
      return;
    }
    if (cache.meetings) {
      setData({ meetings: cache.meetings, actions: cache.actions, loading: false });
    }
    const fresh = await fetchAll();
    setData({ ...fresh, loading: false });
    notifyListeners();
  }, []);

  useEffect(() => { load(refreshTrigger > 0); }, [refreshTrigger, load]); // eslint-disable-line

  return { ...data, reload: () => load(true) };
}

export function computeStats(meetings, actions) {
  const done = meetings.filter(m => m.status === 'done');
  const acts = Array.isArray(actions) ? actions : [];

  // Use duration_s if available, else estimate from transcript word count (~130 words/min speech)
  const minutesAnalysed = done.reduce((sum, m) => {
    if (m.duration_s) return sum + Math.round(m.duration_s / 60);
    if (m.transcript) return sum + Math.max(1, Math.round(m.transcript.split(/\s+/).length / 130));
    return sum + 30; // fallback estimate
  }, 0);
  const minutesSaved = Math.max(1, Math.round(minutesAnalysed * 0.7));

  const totalActs = acts.length;
  const doneActs  = acts.filter(a => a.status === 'done').length;
  const openActs  = acts.filter(a => a.status !== 'done').length;
  const completionRate = totalActs ? Math.round((doneActs / totalActs) * 100) : 0;

  const dayCounts = {};
  done.forEach(m => {
    const day = new Date(m.created_at).toLocaleDateString('en-US', { weekday: 'long' });
    dayCounts[day] = (dayCounts[day] || 0) + 1;
  });
  const mostActiveDay = Object.entries(dayCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '—';

  const meetingDays = new Set(done.map(m => new Date(m.created_at).toDateString()));
  let streak = 0;
  const today = new Date();
  for (let i = 0; i < 30; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    if (meetingDays.has(d.toDateString())) streak++;
    else if (i > 0) break;
  }

  return {
    totalMeetings: done.length,
    openActions: openActs,
    minutesAnalysed,
    minutesSaved,
    completionRate,
    mostActiveDay,
    streak,
    totalActs,
    doneActs,
  };
}
